
import { of as observableOf, Subject } from 'rxjs'

import { mdiFeather } from '@mdi/js'
import { HelpToggleButton } from 'components/App/components/HelpToggleButton'
import EmptyView from 'components/EmptyView'
import PostFilesDialog from 'components/Posts/PostFilesDialog'
import PostDraftView from 'components/Posts/components/PostDraftView'
import ScrollListener from 'components/ScrollListener'
import SimpleLoader from 'components/SimpleLoader'
import { POST_TYPES } from 'interfaces/Content/ContentType'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { NavLink, useLocation, useNavigate } from 'react-router'
import { BulkPostActions } from 'routes/publishing/components/BulkActionsMenus'
import { catchError, mergeMap, switchMap, tap } from 'rxjs/operators'
import { mergeState, setDraft } from 'services/compose'
import { composerResetKeySelector } from 'services/compose/selectors'
import ComposerState from 'services/compose/state'
import { deleteDrafts, getDrafts } from 'services/post/drafts/actions'
import { selectedProfilesPPIdsSelector } from 'services/post/selectors'
import { message } from 'services/snackbar/actions'
import { ContentType } from 'shared'
import { PostDraftData } from 'shared/types'
import { StoreThunkDispatch } from 'store/state'
import styles from './Drafts.pcss'
import PublishingTopBar from './components/PublishingTopBar'

const INFINITE_SCROLL_THRESHOLD = 350

export function DraftsManager() {
  const intl = useIntl()
  const dispatch = useDispatch<StoreThunkDispatch>()
  const location = useLocation()
  const navigate = useNavigate()
  const selectedPPIds = useSelector(selectedProfilesPPIdsSelector).join(',')
  const [drafts, setDrafts] = useState<PostDraftData<ComposerState>[]>([])
  const [selectedDrafts, setSelectedDrafts] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [hasNextPage, setHasNextPage] = useState(true)
  const [totalDraftsCount, setDraftsCount] = useState(0)
  const [mediaPopupImages, setMediaPopupImages] = useState<string[]>([])
  const [refreshKey, setRefreshKey] = useState(0)
  const [search, setSearch] = useState<{ query: string, types: ContentType[], searchInSummary: boolean }>(
    { query: '', types: POST_TYPES, searchInSummary: true }
  )
  const [isEmptyViewClosed, setIsEmptyViewClosed] = useState(false)
  const composerResetKey = useSelector(composerResetKeySelector)
  const scrollElementRef = useRef<HTMLElement | undefined>(undefined)

  useLayoutEffect(() => {
    scrollElementRef.current = document.querySelector('[data-test="main"]') as HTMLElement
  }, [])

  const fetch$ = useRef<Subject<{
    ids: string[], page: number, query?: string, types?: ContentType[], searchInSummary?: boolean
  }>>(undefined)
  const delete$ = useRef<Subject<{ ids: string[], all?: boolean }>>(undefined)

  useEffect(() => {
    setRefreshKey(current => current + 1)
  }, [composerResetKey])

  useEffect(() => {
    if (location.state?.search) {
      setSearch(location.state.search)
    }
  }, [location.state?.search])

  useEffect(() => {
    fetch$.current = new Subject()
    fetch$.current
      .pipe(tap(({ page }) => {
        setLoading(true)
        setPage(page)
      })).pipe(
        switchMap(({ ids, page, query, types, searchInSummary }) => dispatch(getDrafts(ids, page, query, types, searchInSummary)).pipe(
          catchError((e) => {
            console.log('Error fetching drafts', e)
            dispatch(message('Error fetching drafts! Please refresh.', 'error'))
            return observableOf({ error: true })
          })
        )))
      .subscribe((response: any) => {
        setLoading(false)
        setIsEmptyViewClosed(false)
        if (!response.error) {
          setHasNextPage(response.paging.current + 1 < response.paging.pages)
          setDrafts(current => response.paging.page === 0 ? response.drafts : [...current, ...response.drafts])
          setDraftsCount(response.totalDraftsCount)
        }
      })

    return () => fetch$.current?.unsubscribe()
  }, [dispatch])

  useEffect(() => {
    delete$.current = new Subject()
    delete$.current
      .pipe(
        tap(() => {
          dispatch(message(intl.formatMessage({ id: 'post.notifications.deleting-drafts' })))
          setLoading(true)
        }),
        mergeMap(({ ids, all }) => {
          return dispatch(deleteDrafts(ids, selectedPPIds.split(','), search.types, all))
            .pipe(catchError((error: Error) => {
              dispatch(message(
                intl.formatMessage({ id: 'post.notifications.delete-drafts-error' }, { error: error.message }),
                'error'
              ))
              return observableOf({ error })
            }))
        })
      )
      .subscribe(() => {
        setLoading(false)
        setSelectedDrafts([])
        setRefreshKey(current => current + 1)
      })

    return () => delete$.current?.unsubscribe()
  }, [dispatch, intl, search.types, selectedPPIds])

  useEffect(() => {
    fetch$.current?.next({
      ids: selectedPPIds.split(','),
      page: 0,
      query: search.query,
      types: search.types,
      searchInSummary: search.searchInSummary
    })
  }, [refreshKey, search.query, search.types, selectedPPIds, search.searchInSummary])

  const loadNextPage = useCallback(() => {
    if (hasNextPage && !loading) {
      fetch$.current?.next({
        ids: selectedPPIds.split(','),
        page: page + 1,
        ...search
      })
    }
  }, [page, hasNextPage, loading, search, selectedPPIds])

  const onEditDraft = useCallback((data: PostDraftData<ComposerState>) => {
    dispatch(mergeState(data.state))
    dispatch(setDraft({ id: data.id, updatedAt: data.updatedAt }))
    navigate('/composer', { state: { floating: true, modal: 'compose', opener: '/posts/planned/drafts', search, background: location } })
  }, [navigate, dispatch, search, location])

  const deleteDraft = useCallback((id: string) => {
    delete$.current?.next({ ids: [id] })
  }, [])

  const deleteSelected = useCallback(() => {
    delete$.current?.next({ ids: selectedDrafts, all: selectedDrafts.length === totalDraftsCount })
  }, [selectedDrafts, totalDraftsCount])

  const toggleAllSelected = useCallback(() => {
    setSelectedDrafts(current => {
      if (current.length > 0) {
        return []
      }
      return drafts.map(d => d.id)
    })
  }, [drafts])

  const toggleDraftSelected = useCallback((id: string) => {
    setSelectedDrafts(current => {
      const index = current.indexOf(id)
      let next: string[] = []
      if (index === -1) {
        next = [...current, id]
      } else {
        next = [...current]
        next.splice(index, 1)
      }
      return next
    })
  }, [])

  const openMediaPopup = useCallback((photos: string[]) => {
    setMediaPopupImages(photos)
  }, [])

  const closeMediaPopup = () => {
    setMediaPopupImages([])
  }

  const onFilter = useCallback((query: string, types: ContentType[], searchInSummary: boolean) => {
    setSearch({ query, types, searchInSummary })
    setSelectedDrafts([])
  }, [])

  const closeEmptyView = () => {
    setIsEmptyViewClosed(true)
  }

  const isEmpty = !loading && drafts.length === 0

  return (
    <div className={styles['drafts-container']}>
      <PublishingTopBar
        activeTypes={search.types}
        totalCount={totalDraftsCount}
        draftsView
        onFilter={onFilter}
        children={<HelpToggleButton />}
      />
      <div className={styles['actions-box']}>
        {!isEmpty && (
          <BulkPostActions
            withDelete
            disabled={selectedDrafts.length === 0}
            selectedCount={selectedDrafts.length}
            allSelected={selectedDrafts.length === drafts.length}
            hidePostActions={false}
            onToggleAllSelected={toggleAllSelected}
            onBulkDeletePosts={deleteSelected}
          />
        )}
      </div>
      <ScrollListener scrollElement={scrollElementRef.current} emitTreshold={INFINITE_SCROLL_THRESHOLD} onScroll={loadNextPage}>
        <div>
          {drafts.map(draft => (
            <PostDraftView
              key={draft.id}
              draft={draft}
              isSelected={selectedDrafts.includes(draft.id)}
              className={styles.draft}
              onMultiPhotoBtnClick={openMediaPopup}
              onToggleSelected={toggleDraftSelected}
              onEditBtnClick={onEditDraft}
              onDelete={deleteDraft}
            />
          ))}
        </div>
      </ScrollListener>
      <PostFilesDialog
        active={Boolean(mediaPopupImages.length > 0)}
        images={mediaPopupImages}
        onClose={closeMediaPopup}
      />
      {isEmpty && !isEmptyViewClosed && (
        <EmptyView
          icon={mdiFeather}
          title="No saved drafts"
          subtitle={(
            <div>
              <NavLink to={{ pathname: '/composer' }} state={{ floating: true, modal: 'compose', opener: location.pathname, search }}>
                Start a new post
              </NavLink>
              &nbsp;and click “Save as draft” to save it
            </div>
          )}
          backdrop
          backdropClassName={styles['empty-backdrop']}
          onClose={closeEmptyView}
        />
      )}
      {loading && (
        <div className={styles['loading-overlay']}>
          <SimpleLoader small />
        </div>
      )}
    </div>

  )
}

export default DraftsManager
